<template>
  <div>
    <kn-jumbotron></kn-jumbotron>
    <b-container fluid class="px-4 pb-5">
      <b-row class="justify-content-center mx-auto" style="max-width: 1400px;">
        <kn-sideMenu></kn-sideMenu>

        <main role="main" class="col-md-9 pl-md-5">
          <div class="content-card">

            <div class="mb-4">
              <router-link :to="getDecadeLink()" class="back-link">
                <i class="fa-solid fa-arrow-left mr-2"></i>Επιστροφή στη Δεκαετία {{ decade }}
              </router-link>
            </div>

            <h2 class="section-title">
              <span class="blue-accent"></span>Έτος {{ year }}
            </h2>

            <div class="accordion-wrapper mt-4">
              <b-card no-body class="modern-accordion-card mb-3" v-for="(monthPair, index) in monthPairs" :key="index">
                <b-card-header header-tag="header" class="p-0 border-0" role="tab">
                  <b-btn block href="#" v-b-toggle="`accordion-${index}`" class="modern-accordion-btn text-left">
                    <i class="fa-regular fa-calendar mr-2"></i> {{ monthPair.label }}
                    <i class="fa-solid fa-chevron-down float-right mt-1 chevron-icon"></i>
                  </b-btn>
                </b-card-header>

                <b-collapse :id="`accordion-${index}`" accordion="my-accordion" role="tabpanel">
                  <b-card-body class="p-4 bg-light">
                    <div class="pdf-toolbar mb-3 d-flex justify-content-center justify-content-md-end">
                      <a :href="getPdfUrl(monthPair.file, year)" target="_blank"
                        class="btn btn-sm btn-outline-primary pdf-btn">
                        <i class="fa-solid fa-expand mr-1"></i> Προβολή σε πλήρη οθόνη
                      </a>
                    </div>
                    <div class="pdf-wrapper">
                      <iframe class="modern-pdf-iframe" :src="getPdfUrl(monthPair.file, year)" allowfullscreen>
                      </iframe>
                    </div>
                  </b-card-body>
                </b-collapse>
              </b-card>
            </div>

          </div>
        </main>
      </b-row>
    </b-container>
    <kn-footer></kn-footer>
  </div>
</template>

<script>
import fileMappings from "@/assets/data/file-mappings.json";
import jumbotron from './jumbotron.vue';
import sideMenu from './sideMenu.vue';
import footer from './footer.vue';

export default {
  components: {
    "kn-jumbotron": jumbotron,
    "kn-sideMenu": sideMenu,
    "kn-footer": footer
  },
  props: {
    year: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      latestYear: null,
      monthsAvailable: 6
    }
  },
  mounted() {
    // Automatically fetch the latest issue config with a cache-busting query parameter
    fetch("https://www.kimoliaka-nea.gr/efimerides/latest-issue.json?t=" + new Date().getTime())
      .then(response => {
        if (!response.ok) throw new Error("Fetch failed");
        return response.json();
      })
      .then(data => {
        if (data && data.latestYear) {
          this.latestYear = data.latestYear;
          this.monthsAvailable = data.monthsAvailable || 6;
        }
      })
      .catch(err => {
        console.warn("Using fallback decade logic due to missing remote config file:", err);
      });
  },
  computed: {
    monthPairs() {
      const allPairs = [
        { label: 'Ιανουάριος - Φεβρουάριος', file: 'IAN-FEBR' },
        { label: 'Μάρτιος - Απρίλιος', file: 'MART-APRL' },
        { label: 'Μάϊος - Ιούνιος', file: 'MAIOS-IOYN' },
        { label: 'Ιούλιος - Αύγουστος', file: 'IOUL-AYG' },
        { label: 'Σεπτέμβριος - Οκτώβριος', file: 'SEPT-OKT' },
        { label: 'Νοέμβριος - Δεκέμβριος', file: 'NOEM-DEK' }
      ];

      // If we successfully fetched the active remote year config
      if (this.latestYear) {
        if (this.year === this.latestYear) {
          return allPairs.slice(0, this.monthsAvailable);
        } else if (this.year > this.latestYear) {
          return []; // This year hasn't happened yet!
        }
        return allPairs; // For older years (e.g. clicked 1990 while in 2026), show all 6
      }

      // Safe Fallback if the fetch completely fails or offline
      if (this.year === 2025) {
        return allPairs.slice(0, 6); // Hardcoded fallback for NOEM-DEK 2025
      }

      return allPairs;
    },
    // Compute the decade the year belongs to
    decade() {
      return Math.floor(this.year / 10) * 10; // Example: 2007 => 2000
    }
  },
  methods: {
    getPdfUrl(filePrefix, year) {
      // Check if the year has mappings in the JSON file
      if (fileMappings[year] && fileMappings[year][filePrefix]) {
        const fileId = fileMappings[year][filePrefix];
        return `https://drive.google.com/file/d/${fileId}/preview`;
      }

      // Fallback to default web host URL for files not in the JSON
      return `https://www.kimoliaka-nea.gr/efimerides/efimerida_${filePrefix}_${year}.pdf`;
    },
    // Generate the link to the decade page
    getDecadeLink() {
      return `/${this.decade}`; // Routes to the corresponding decade page
    }
  }
}
</script>

<style lang="scss" scoped>
.content-card {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 10px 25px -5px rgba(2, 132, 199, 0.1), 0 8px 10px -6px rgba(2, 132, 199, 0.05);
  padding: 2.5rem;
  margin-bottom: 2rem;
  border-top: 5px solid #0284c7;
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 1.75rem;
  color: #0c4a6e;
  border: none;
  margin-bottom: 1.5rem;
  font-weight: 700;
}

.blue-accent {
  background: #0ea5e9;
  display: inline-block;
  width: 6px;
  height: 28px;
  margin-right: 12px;
  border-radius: 4px;
}

.back-link {
  display: inline-flex;
  align-items: center;
  color: #0c4a6e;
  font-weight: 600;
  text-decoration: none;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  padding: 0.5rem 0;
}

.back-link:hover {
  color: #0284c7;
  transform: translateX(-5px);
}

.modern-accordion-card {
  border: 1px solid #e0f2fe;
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(2, 132, 199, 0.05);
}

.modern-accordion-btn {
  background-color: #f8fafc !important;
  color: #0c4a6e !important;
  border: none;
  padding: 1.25rem 1.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: none !important;
  border-radius: 0;
}

.modern-accordion-btn:hover,
.modern-accordion-btn:focus,
.modern-accordion-btn:active {
  background-color: #f0f9ff !important;
  color: #0284c7 !important;
}

.collapsed .chevron-icon {
  transform: rotate(0deg);
  transition: transform 0.3s ease;
}

.chevron-icon {
  transform: rotate(180deg);
  transition: transform 0.3s ease;
  color: #0ea5e9;
}

/* PDF Modernization Styles */
.pdf-toolbar {
  border-bottom: 1px solid #e0f2fe;
  padding-bottom: 0.5rem;
}

.pdf-btn {
  color: #0284c7;
  border-color: #0284c7;
  font-weight: 500;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.pdf-btn:hover {
  background-color: #0284c7;
  color: #ffffff;
}

@media (max-width: 767.98px) {
  .pdf-btn {
    width: 100%;
    padding: 0.6rem;
    font-size: 1rem;
  }
}

.pdf-wrapper {
  width: 100%;
  /* A4 paper aspect ratio is ~ 1:1.414 */
  aspect-ratio: 1 / 1.414;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  border: 4px solid #f8fafc;
  background-color: #f1f5f9;
  position: relative;
}

/* Fallback for older browsers that don't support aspect-ratio */
@supports not (aspect-ratio: 1 / 1.414) {
  .pdf-wrapper {
    height: 80vh;
  }
}

.modern-pdf-iframe {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
}
</style>


// WEBPACK FOOTER //
// src/components/yearComponent.vue